import type { FmElement } from '../elements/elements.types';
import type { FmSection } from './section.types';

/** Every element a section shows, in reading order: slots first, then tab panels or slides. */
export function sectionElements(section: FmSection): readonly FmElement[] {
  if ('lead' in section && section.lead) return [section.lead];
  if (!('slots' in section) || !section.slots) return [];
  const slotted = Object.values(section.slots).flatMap(
    (elements: readonly FmElement[] | undefined) => elements ?? [],
  );
  if (section.arrangement === 'tabbed' && section.panels) {
    return [...slotted, ...section.panels.flatMap((panel) => panel.elements)];
  }
  if (section.arrangement === 'carousel' && section.slides) {
    return [...slotted, ...section.slides.flatMap((slide) => slide.elements)];
  }
  return slotted;
}

/**
 * The heading a section prints first: its first copy element's heading. A
 * chapter compares these to settle sibling repeats, which no single section can
 * see from inside itself.
 */
export function sectionLeadHeading(section: FmSection): string | undefined {
  const copy = sectionElements(section).find(
    (element): element is Extract<FmElement, { element: 'copy' }> =>
      element.element === 'copy' && Boolean(element.heading),
  );
  return copy?.heading;
}

/** A section owns a primary action when it shows one or when its locked state carries the unlock step. */
export function sectionOwnsPrimaryAction(section: FmSection): boolean {
  const { state } = section;
  if (state.status === 'locked' && state.action) return true;
  return sectionElements(section).some(
    (element) => element.element === 'actions' && element.primary !== undefined,
  );
}
